import React from 'react';
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';

type Props = {
    data: { timestamp: string; value: number }[];
    color?: string;
    label: string;
};


function prepareChartData(
    data: { timestamp: string; value: number }[]
): { date: string; value: number }[] {
    const today = new Date();
    const startDate = new Date(today);
    startDate.setMonth(today.getMonth() - 1);

    // 日付キー化した入力データ（YYYY-MM-DD）
    const dataMap = new Map<string, number>();
    data.forEach(d => {
        const key = new Date(d.timestamp).toISOString().slice(0, 10);
        dataMap.set(key, d.value);
    });

    const result: { date: string; value: number }[] = [];
    let lastKnownValue = 0;

    for (
        let d = new Date(startDate);
        d <= today;
        d.setDate(d.getDate() + 1)
    ) {
        const key = d.toISOString().slice(0, 10);
        const displayDate = d.toLocaleDateString("ja-JP", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit"
        });

        if (dataMap.has(key)) {
            lastKnownValue = dataMap.get(key)!;
        }

        result.push({
            date: displayDate,
            value: lastKnownValue
        });
    }

    return result;
}

const MetricLineChart: React.FC<Props> = ({ data, color = '#3182ce', label }) => {
    // データの整形（表示用日付付き）
    const formattedData = prepareChartData(data);

    return (
        <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={formattedData}>
                <defs>
                    <linearGradient id={`gradient-${label}`} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={color} stopOpacity={0.6} />
                        <stop offset="95%" stopColor={color} stopOpacity={0} />
                    </linearGradient>
                </defs>

                {/* Y軸の罫線を非表示 */}
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />

                <XAxis
                    dataKey="date"
                    tickFormatter={(date) => {
                        const d = new Date(date);
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        return `${month}/${day}`;
                    }}
                    tick={{ fontSize: 10 }}
                />

                {/* Y軸ラベルのフォントサイズを小さく */}
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />

                <Tooltip />

                <Area
                    type="monotone"
                    dataKey="value"
                    stroke={color}
                    fillOpacity={1}
                    fill={`url(#gradient-${label})`}
                    name={label}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default MetricLineChart;